import { LitElement, html } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-vehicle-and-loan-mfe';
import { nothing } from 'lit-html';
import { DebugService } from '../../../../shared/services/debug.service.js';
import { currencyFormatter } from '../../../../shared/formatters/currency.formatter.js';
import { REFI_VEHICLE_LOAN_CONST } from '../../../../shared/constants/app.constants.js';
import { POLICY_LIMITS_CONST } from '../../../../shared/services/policy-limits.service.js';
import { style } from './iconic-loan-inputs.style.js';
import { globalStyle } from '../../../../global-styles.style.js';
import { links } from '../../../../shared/styles/links.style.js';
import { LoanService } from '../../../../services/loan.service.js';
import { VehicleService } from '../../../../services/vehicle.service.js';
import { stratum } from '../../../../../constants/stratum.js';
import { tags } from '../../../../shared/constants/tags.js';
import { ApplicationService } from '../../../../services/application.service.js';

class RefiIconicLoanInputs extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiIconicLoanInputs');
    this.debug.log('loaded with', this.loanObject);

    this.values = {};

    this.hasSentCurBalTag = false;
    this.hasSentLtvTag = false;
    this.maxMonthlyPayment = POLICY_LIMITS_CONST.MAX_MONTHLY_PAYMENT();
    this.minRemainingTerm = POLICY_LIMITS_CONST.MIN_REMAINING_TERM();
    this.maxRemainingTerm = POLICY_LIMITS_CONST.MAX_REMAINING_TERM();

    this.isInvalidMonthlyPayment = false;
    this.isInvalidPayoff = false;
    this.isHighLtv = false;
    this.isInvalidTerm = false;

    this.capitalOneLoanErrorMessage = '';
    this.leaseErrorMessage = '';
    this.monthlyPaymentErrorMessage = '';
    this.payoffErrorMessage = '';
    this.termErrorMessage = '';
  }

  static styles = [style, globalStyle, links];

  static properties = {
    loanObject: { type: Object, reflect: true },
    monthlyPayment: { type: Object },
    payoffAmount: { type: Object },
    maxMonthlyPayment: { type: Number },
    hasSentMnthPayTag: { type: Boolean },
    isInvalidMonthlyPayment: { type: Boolean },
    isInvalidPayoff: { type: Boolean },
    isInvalidTerm: { type: Boolean },
    showBadMathWarning: { type: Boolean },
    isHighLtv: { type: Boolean },
    capitalOneLoanErrorMessage: { type: String },
    leaseErrorMessage: { type: String },
    monthlyPaymentErrorMessage: { type: String },
    payoffErrorMessage: { type: String },
    termErrorMessage: { type: String },
  };

  connectedCallback() {
    this.maxMonthlyPayment = LoanService.getMaximumMonthlyPayment();
    super.connectedCallback();

    this.values = JSON.parse(JSON.stringify(this.loanObject));
  }

  runBusinessChecks() {
    const isCapitalOneLoanValue =
      this.shadowRoot.getElementById('isCapOneLoan')?.value;
    const isLeaseValue = this.shadowRoot.getElementById('isLease').value;
    this.invalidLeaseCheck({ target: { value: isLeaseValue } });
    this.invalidCapitalOneLoanCheck({
      target: { value: isCapitalOneLoanValue },
    });
    this.invalidMonthlyPaymentCheck();
    this.invalidPayoffCheck();
    this.invalidTermCheck();
    this.calcEstLoanAmount();
  }

  updateLoanObject() {
    this.loanObject = this.values;
    this.requestUpdate();
  }

  render() {
    return html`
      <div class="grid">
        <div data-e2e-ref="is-capital-one-loan-field">
          <fs-select
            id="isCapOneLoan"
            value="${this.values?.isCapitalOneLoaned}"
            error-message="${this.capitalOneLoanErrorMessage}"
            @blur="${this.invalidCapitalOneLoanCheck}"
            @input="${(ev) =>
              this.changedString(
                'isCapitalOneLoaned',
                ev,
                ev.target?.value.replace(/[$,]/g, '')
              )}"
            required
          >
            <span slot="label"
              >Is this car currently financed by Capital One?</span
            >
            <option value="">Select</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </fs-select>
        </div>
        <div data-e2e-ref="is-lease-field">
          <fs-select
            id="isLease"
            value="${this.values?.isLease}"
            error-message="${this.leaseErrorMessage}"
            @blur="${this.invalidLeaseCheck}"
            @input="${(ev) =>
              this.changedString(
                'isLease',
                ev,
                ev.target?.value.replace(/[$,]/g, '')
              )}"
            required
          >
            <span slot="label">Is this car under a lease agreement?</span>
            <option value="">Select</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </fs-select>
        </div>
        <div data-e2e-ref="monthly-payment-field">
          <fs-currency-field
            id="monthlyPayment"
            name="monthlyPayment"
            maxLength="5"
            pattern="\\$[1-9].*"
            inputmode="numeric"
            maximum-fraction-digits="0"
            minimum-decimal-precision="0"
            minimum-fraction-digits="0"
            decimal-separator="none"
            required
            @input="${(ev) =>
              this.changed(
                'monthlyPayment',
                ev,
                ev.target?.value.replace(/[$,]/g, '')
              )}"
            @blur="${this.invalidMonthlyPaymentCheck}"
            value="${this.values?.monthlyPayment}"
            error-message="${this.monthlyPaymentErrorMessage}"
            value-missing-message="Please enter a valid monthly payment"
            pattern-mismatch-message="Amount entered should be greater than $0"
          >
            Monthly payment
          </fs-currency-field>
        </div>
        <div data-e2e-ref="payoff-amount-field">
          <fs-currency-field
            id="payoffAmount"
            name="payoffAmount"
            maxLength="7"
            inputmode="numeric"
            pattern="\\$[1-9].*"
            maximum-fraction-digits="0"
            minimum-decimal-precision="0"
            minimum-fraction-digits="0"
            decimal-separator="none"
            required
            value="${this.values?.currentBalance}"
            @input="${(ev) =>
              this.changed(
                'currentBalance',
                ev,
                ev.target?.value.replace(/[$,]/g, '')
              )}"
            @blur="${this.invalidPayoffCheck}"
            error-message="${this.payoffErrorMessage}"
            value-missing-message="Please enter a valid payoff amount"
            pattern-mismatch-message="Amount entered should be greater than $0"
          >
            Payoff amount
          </fs-currency-field>
        </div>
        <div data-e2e-ref="remaining-term-field" class="term">
          <fs-text-field
            id="remainingTerm"
            name="remainingTerm"
            maxLength="2"
            decimal-separator="none"
            pattern="${!ApplicationService.isInAppDeclineDelayOn
              ? '^([1-9]|[1-9][0-9])$'
              : nothing}"
            value="${this.values?.remainingTerm}"
            required
            @blur="${this.invalidTermCheck}"
            @input="${(ev) =>
              this.changed('remainingTerm', ev, ev.target?.value)}"
            error-message="${this.termErrorMessage}"
            helper-text="${!ApplicationService.isInAppDeclineDelayOn
              ? `Your remaining term must be at least ${this.minRemainingTerm} months`
              : nothing}"
            value-missing-message="Please enter a valid term length"
            pattern-mismatch-message="Please enter a term between ${this
              .minRemainingTerm} months and ${this.maxRemainingTerm} months"
          >
            Remaining term length (months)
          </fs-text-field>
        </div>
      </div>
      ${this.showBadMathWarning
        ? html`
            <fs-alert class="alert" type="error" role="alert" aria-atomic="true"
              >Double check that your info has been entered correctly. The
              numbers entered indicate your APR is less than 0%.</fs-alert
            >
          `
        : ''}
    `;
  }

  firstUpdated() {
    this.values.paymentDueDay = 15;
    this.values.ineligibleReasons = [];
  }

  changedString(name, ev, value = ev.target?.value) {
    if (value === '') {
      this.values[name] = null;
      /* eslint-disable no-param-reassign */
      ev.target.value = '';
    } else {
      this.values[name] = value;
    }
  }

  changed(name, ev, value = ev.target?.value) {
    if (value === '') {
      this.values[name] = null;
      /* eslint-disable no-param-reassign */
      ev.target.value = '';
    } else {
      const removeDecimals = value.toString().split('.');
      this.values[name] = +removeDecimals[0];
      if (removeDecimals[1]) {
        this.requestUpdate();
      }
    }

    this.calcEstLoanAmount();
  }

  calcEstLoanAmount() {
    this.debug.log('calcEstLoanAmount called', this.values);
    if (
      Number(this.values?.remainingTerm) > 11 &&
      this.values?.monthlyPayment &&
      this.values?.currentBalance
    ) {
      this.debug.log('calcEstLoanAmount calculating');
      this.estLoanPayoff =
        this.values?.remainingTerm * this.values?.monthlyPayment;
      this.showBadMathWarning =
        this.estLoanPayoff < this.values?.currentBalance;
      this.debug.log(
        'calcEstLoanAmount showWarning: ',
        this.showBadMathWarning
      );
      if (this.showBadMathWarning && !this.hasSentBadMathTag) {
        REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
          tags.shared.loan.current.negativeApr,
          '',
          'error',
          'message'
        );
        stratum.publishTag(
          TagKey.MANUAL_LOAN_ENTRY_MANUAL_LOAN_FIELD_VALIDATION_ERROR,
          { replacements: { value: tags.shared.loan.current.negativeApr } }
        );

        this.hasSentBadMathTag = true;
      }
    } else {
      this.showBadMathWarning = false;
    }

    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: {
          name: 'estLoanAmount',
          isValid: !this.showBadMathWarning,
        },
      })
    );
  }

  invalidLeaseCheck(e) {
    this.leaseErrorMessage = '';
    if (e.target.value === 'yes' && !ApplicationService.isInAppDeclineDelayOn) {
      this.leaseErrorMessage = 'We do not refinance leases';
    }
  }

  invalidCapitalOneLoanCheck(e) {
    this.capitalOneLoanErrorMessage = '';
    if (e.target.value === 'yes' && !ApplicationService.isInAppDeclineDelayOn) {
      this.capitalOneLoanErrorMessage =
        'At this time we do not refinance auto loans that are already under Capital One';
    }
  }

  invalidMonthlyPaymentCheck() {
    const monthlyPaymentField =
      this.shadowRoot.getElementById('monthlyPayment');
    if (!ApplicationService.isInAppDeclineDelayOn) {
      stratum.publishTag(TagKey.MANUAL_LOAN_ENTRY_MANUAL_ENTRY_MONTHLY_PMT, {
        replacements: {
          value: this.values.monthlyPayment,
        },
      });

      this.maxMonthlyPayment = LoanService.getMaximumMonthlyPayment();
      this.isInvalidMonthlyPayment =
        this.values.monthlyPayment &&
        this.values.monthlyPayment > this.maxMonthlyPayment;

      const valid =
        !this.isInvalidMonthlyPayment &&
        monthlyPaymentField.value !== '' &&
        this.values.monthlyPayment !== 0;

      if (this.isInvalidMonthlyPayment) {
        stratum.publishTag(
          TagKey.MANUAL_LOAN_ENTRY_MANUAL_LOAN_FIELD_VALIDATION_ERROR,
          { replacements: { value: tags.shared.loan.current.paymentHigh } }
        );
        this.monthlyPaymentErrorMessage = `Please enter a current monthly payment of up to ${currencyFormatter(
          this.maxMonthlyPayment
        )}`;
      } else if (monthlyPaymentField.value === '') {
        this.monthlyPaymentErrorMessage =
          'Please enter a valid monthly payment';
      } else if (this.values.monthlyPayment === 0) {
        stratum.publishTag(
          TagKey.MANUAL_LOAN_ENTRY_MANUAL_LOAN_FIELD_VALIDATION_ERROR,
          { replacements: { value: tags.shared.loan.current.paymentLow } }
        );
        this.monthlyPaymentErrorMessage =
          'Amount entered should be greater than $0';
      } else {
        this.monthlyPaymentErrorMessage = '';
      }
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'monthlyPayment',
            isValid: valid,
          },
        })
      );

      if (!valid && !this.hasSentMnthPayTag) {
        const analyStr =
          this.values.monthlyPayment > this.maxMonthlyPayment
            ? 'error mthly pmt too high'
            : 'error mnthly pmt 0';
        REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
          analyStr,
          this.values.monthlyPayment,
          'error',
          'message'
        );
        this.hasSentMnthPayTag = true;
      }
    } else {
      if (monthlyPaymentField.value === '') {
        this.monthlyPaymentErrorMessage =
          'Please enter a valid monthly payment';
      } else if (this.values.monthlyPayment === 0) {
        this.monthlyPaymentErrorMessage =
          'Amount entered should be greater than $0';
      } else {
        this.monthlyPaymentErrorMessage = '';
      }
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'monthlyPayment',
            isValid:
              monthlyPaymentField.value !== '' &&
              this.values.monthlyPayment !== 0,
          },
        })
      );
    }
  }

  invalidPayoffCheck() {
    const payoffField = this.shadowRoot.getElementById('payoffAmount');

    if (!ApplicationService.isInAppDeclineDelayOn) {
      stratum.publishTag(TagKey.MANUAL_LOAN_ENTRY_MANUAL_ENTRY_BALANCE, {
        replacements: {
          value: this.values.currentBalance,
        },
      });
      const minimumBalanceAmount = LoanService.getMinimumBalanceAmount();
      const maximumBalanceAmount = LoanService.getMaximumBalanceAmount();

      this.isInvalidPayoff =
        this.values.currentBalance &&
        (this.values.currentBalance < minimumBalanceAmount ||
          this.values.currentBalance > maximumBalanceAmount);

      const wholeSaleValue = Number(
        VehicleService.selectedVehicle.wholeSaleValue
      );
      this.isHighLtv =
        Number(this.values.currentBalance) / wholeSaleValue >
        POLICY_LIMITS_CONST.MAX_LTV();
      const valid =
        !this.isInvalidPayoff &&
        !this.isHighLtv &&
        payoffField.value !== '' &&
        this.values.currentBalance !== 0;

      if (this.isInvalidPayoff) {
        this.payoffErrorMessage =
          'This amount is outside of Capital One Auto Refinance’s amount financed limits for your loan';
      } else if (this.isHighLtv) {
        this.payoffErrorMessage =
          'The payoff amount for this loan is too high for this vehicle';
        stratum.publishTag(
          TagKey.MANUAL_LOAN_ENTRY_MANUAL_LOAN_FIELD_VALIDATION_ERROR,
          { replacements: { value: tags.shared.loan.current.balanceHigh } }
        );
      } else if (payoffField.value === '') {
        this.payoffErrorMessage = 'Please enter a valid payoff amount';
      } else if (this.values.currentBalance === 0) {
        this.payoffErrorMessage = 'Amount entered should be greater than $0';
      } else {
        this.payoffErrorMessage = '';
      }
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'payoffAmount',
            isValid: valid,
          },
        })
      );

      if (this.values.currentBalance && this.isHighLtv && !this.hasSentLtvTag) {
        REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
          tags.shared.loan.current.manualHighLtv,
          this.values.currentBalance,
          'error',
          'message'
        );
        stratum.publishTag(
          TagKey.MANUAL_LOAN_ENTRY_MANUAL_LOAN_FIELD_VALIDATION_ERROR,
          { replacements: { value: tags.shared.loan.current.manualHighLtv } }
        );
        this.hasSentLtvTag = true;
      }

      if (!valid && !this.hasSentCurBalTag) {
        const balanceHighTag = 'error balance too high';
        const balanceLowTag = 'error balance too low';
        const analyStr =
          this.values.currentBalance > maximumBalanceAmount
            ? balanceHighTag
            : balanceLowTag;
        REFI_VEHICLE_LOAN_CONST.TRACK_EVENT(
          analyStr,
          this.values.currentBalance,
          'error',
          'message'
        );
        stratum.publishTag(
          TagKey.MANUAL_LOAN_ENTRY_MANUAL_LOAN_FIELD_VALIDATION_ERROR,
          { replacements: { value: analyStr } }
        );
        this.hasSentCurBalTag = true;
      }
    } else {
      if (payoffField.value === '') {
        this.payoffErrorMessage = 'Please enter a valid payoff amount';
      } else if (this.values.currentBalance === 0) {
        this.payoffErrorMessage = 'Amount entered should be greater than $0';
      } else {
        this.payoffErrorMessage = '';
      }

      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'payoffAmount',
            isValid:
              payoffField.value !== '' && this.values.currentBalance !== 0,
          },
        })
      );

      // tagging
    }
  }

  invalidTermCheck() {
    const termField = this.shadowRoot.getElementById('remainingTerm');
    if (!ApplicationService.isInAppDeclineDelayOn) {
      stratum.publishTag(TagKey.MANUAL_LOAN_ENTRY_MANUAL_ENTRY_TERM, {
        replacements: {
          value: this.values.remainingTerm,
        },
      });
      this.isInvalidTerm =
        this.values.remainingTerm &&
        (this.values.remainingTerm < POLICY_LIMITS_CONST.MIN_REMAINING_TERM() ||
          this.values.remainingTerm > POLICY_LIMITS_CONST.MAX_REMAINING_TERM());

      const valid =
        !this.isInvalidTerm &&
        termField.value !== '' &&
        this.values.remainingTerm !== 0;

      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'remainingTerm',
            isValid: valid,
          },
        })
      );
      if (termField.value === '') {
        this.termErrorMessage = 'Please enter a valid remaining term';
      } else if (this.isInvalidTerm || this.values.remainingTerm === 0) {
        this.termErrorMessage = `Please enter a term between ${this.minRemainingTerm} months and ${this.maxRemainingTerm} months`;
      } else {
        this.termErrorMessage = '';
      }
    } else {
      if (termField.value === '' || this.values.remainingTerm === 0) {
        this.termErrorMessage = 'Please enter a valid remaining term';
      } else {
        this.termErrorMessage = '';
      }
      this.dispatchEvent(
        new CustomEvent('validity', {
          detail: {
            name: 'remainingTerm',
            isValid: termField.value !== '' && this.values.remainingTerm !== 0,
          },
        })
      );
    }
  }
}

customElements.define('refi-iconic-loan-inputs', RefiIconicLoanInputs);
